import { Injectable } from '@angular/core';
import { FormGroup, FormControl, ValidationErrors } from '@angular/forms';

import { EMPTY } from './consts';
import { PortalSandbox } from 'app/modulos/portal/portal.sandbox';

interface IDateFormatted {
  year: number;
  month: number;
  day: number;
}

const REGEX_UNDERSCORE = /_/g;
const ERROR_REQUIRED = 'Este campo es obligatorio';

// tslint:disable-next-line:max-line-length
const EMAIL_REGEXP = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,3}))$/;

const NUMBER_CELULAR_REGEXP = /^9/;

const NUMBER_WITH_DECIMAL_REGEXP = /^[0-9]+([.][0-9]+)?$/g;

@Injectable()
export class ValidationService {
  public validateEmail(formControl: FormControl): { [error: string]: any } {
    if (formControl.value === EMPTY || formControl.value === null) {
      return null;
    }

    let subdominios = false;
    const dominio = formControl.value.split('@')[1];

    if (dominio) {
      subdominios = dominio.split('.').length <= 3;
    }

    return EMAIL_REGEXP.test(formControl.value) && subdominios
      ? null
      : { validateEmail: { valid: false } };
  }

  public validateEmailList(formControl: FormControl): { [error: string]: any } {
    if (formControl.value === EMPTY || formControl.value === null) {
      return null;
    }

    const emailList = formControl.value.split(',');

    for (let i = 0; i < emailList.length; i++) {
      const valor = emailList[i];
      let subdominios = false;
      const dominio = valor.split('@')[1];

      if (dominio) {
        subdominios = dominio.split('.').length <= 3;
      }

      if (!EMAIL_REGEXP.test(valor) || !subdominios) {
        return { validateEmail: { valid: false } };
      }
    }

    return null;
  }

  public validateCelular (formControl: FormControl): ValidationErrors | null  {
    if (formControl.value === EMPTY || formControl.value === null) {
      return null;
    }

    return NUMBER_CELULAR_REGEXP.test(formControl.value) ? null : { validateCelular: { valid: false }};
  }

  public validateNumber({ value }: FormControl): { [error: string]: any } {
    if (value === EMPTY || value === null) {
      return null;
    }
    if (Number(value).toString() === 'NaN') {
      return {
        number: true
      };
    }
  }

  public validateMonto({ value }: FormControl) {
    if (NUMBER_WITH_DECIMAL_REGEXP.test(value)) {
      const ingreso = value.toString();
      const ingresoEnteros = ingreso.split('.')[0];
      const ingresoDecimales = ingreso.split('.')[1];

      if (Number(ingreso) < 0) {
        return { negativo: true };
      } else if (ingresoEnteros.length > 15) {
        return { enteros: true };
      } else if (ingresoDecimales && ingresoDecimales.length > 2) {
        return { decimales: true };
      } else {
        return null;
      }
    }
  }

  public validateDecimalNumber({ value }: FormControl) {
    const NUM_ENTEROS = 6, NUM_DECIMALES = 2;
    let ingresoEnteros, ingresoDecimales;
    const error = {
      enteros: false,
      decimales: false,
    };

    if (Number(value).toString() !== 'NaN') {
      const ingreso = value.toString();
      if (ingreso.includes('.')) {
        ingresoEnteros = ingreso.split('.')[0];
        ingresoDecimales = ingreso.split('.')[1];
        if (ingresoDecimales.length > NUM_DECIMALES) {
          error.decimales = true;
        }
      } else {
        ingresoEnteros = ingreso;
      }
      if (ingresoEnteros.length > NUM_ENTEROS) {
        error.enteros = true;
      }

      if (error.enteros) {
        if (error.decimales) {
          return {
            enteros: true,
            decimales: true,
          };
        } else {
          return {
            enteros: true,
          };
        }
      } else {
        if (error.decimales) {
          return {
            decimales: true,
          };
        }
      }
    }
  }

  public validateNotOnlySpaces({ value }: FormControl) {
    if (value.toString().trim().length === 0) {
      return {
        soloEspacios: true,
      };
    }
  }

  public validateNoEmpty({ value }: FormControl) {
    if (value !== null) {
      if (value.trim().length === 0) {
        return {
          soloEspacios: true,
        };
      }
    }

  }

  public matchingInputs(controlKey: string, matchingControlKey: string) {
    return (group: FormGroup): { [error: string]: any } => {
      const controls = group.controls;
      const controlFirst = controls[controlKey];
      const controlMatch = controls[matchingControlKey];
      if (!controlFirst || !controlMatch) {
        return null;
      }
      if (controlFirst.value !== controlMatch.value) {
        return { mismatch: { valid: false } };
      }
    };
  }

  private convertFormattedToDate = ({ year, month, day }: IDateFormatted) => {
    return new Date(year, month, day);
  };

  private isValidDate = ({ year, month, day }: IDateFormatted) => {
    const date = new Date(year, month, day);
    if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
      return true;
    }
    return false;
  };

  private getDateObject(str: string): IDateFormatted {
    const dateArr = str.split('/').map(Number);
    return {
      year: dateArr[2],
      month: dateArr[1] - 1,
      day: dateArr[0]
    };
  }

  public fechaNacimiento = (min: number, max?: number) => (
    formControl: FormControl
  ): { descripcion: string } => {
    const errorFechaValidacion = this.fechaValidation(formControl);
    if (errorFechaValidacion) {
      return errorFechaValidacion;
    }
    const value = formControl.value;
    const filteredSlash = value.replace(REGEX_UNDERSCORE, '');
    const dateFormatted = this.getDateObject(filteredSlash);

    const date = this.convertFormattedToDate(dateFormatted);
    const now = new Date();

    if (date > now) {
      return { descripcion: 'La fecha de nacimiento debe ser menor a la fecha actual' };
    }
    let subtractDate = new Date();

    subtractDate.setFullYear(subtractDate.getFullYear() - min);
    if (date > subtractDate) {
      return {
        descripcion: `Debe tener como mínimo ${min} años`
      };
    }

    subtractDate = new Date();
    subtractDate.setFullYear(subtractDate.getFullYear() - Number(max));

    if (date < subtractDate) {
      return { descripcion: `Debe tener como máximo ${max} años` };
    }

    return null;
  };

  public fechaMaximaActual = (formControl: FormControl): { descripcion: string } => {
    const errorFechaValidacion = this.fechaValidation(formControl);
    if (errorFechaValidacion) {
      return errorFechaValidacion;
    }
    const value = formControl.value;
    const filteredSlash = value.replace(REGEX_UNDERSCORE, '');
    const dateFormatted = this.getDateObject(filteredSlash);

    const date = this.convertFormattedToDate(dateFormatted);
    const now = new Date();
    this.setearTiempoACero(now);
    if (date >= now) {
      return { descripcion: 'La fecha debe ser menor a la fecha actual' };
    }
    return null;
  };

  private setearTiempoACero(fecha) {
    fecha.setHours(0, 0, 0, 0).toString();
  }

  public fechaValidation = ({ value }: FormControl): { descripcion: string } => {
    if (!value || value === EMPTY) {
      return {
        descripcion: ERROR_REQUIRED
      };
    }
    const filteredSlash = value.replace(REGEX_UNDERSCORE, '');

    if (filteredSlash.length === 10) {
      const dateFormatted = this.getDateObject(filteredSlash);
      if (this.isValidDate(dateFormatted)) {
        return null;
      }
      return {
        descripcion: 'Completar con una fecha válida'
      };
    } else {
      return {
        descripcion: 'Este campo es obligatorio'
      };
    }
  };

  public direccionValidation = (portalSandbox: PortalSandbox) => (
    group: FormGroup
  ): { [key: string]: any } => {
    const { idPais, idDepartamento, idProvincia, idDistrito } = group.controls;

    if (idPais) {
      if (idPais.value === EMPTY) {
        return { idPais: true };
      }
      if (idPais.value !== portalSandbox.ID_PERU) {
        return null;
      }
    }

    if (idDepartamento.value === EMPTY) {
      return { idDepartamento: true };
    }
    if (idProvincia.value === EMPTY) {
      return { idProvincia: true };
    }
    if (idDistrito.value === EMPTY) {
      return { idDistrito: true };
    }
  };

  public ceroPrimerDigitoValidation({ value }: FormControl): { [error: string]: any } {
    if (value && value[0] === '0') {
      return {
        descripcion: 'No debe empezar el monto con cero'
      };
    }
  }

  public esTrue({ value }: FormControl): { [error: string]: boolean } {
    if (!value) {
      return {
        invalido: true
      };
    }
  }

  public isNull({ value }: FormControl): { [error: string]: boolean} {
    if ( value === 'NULL') {
      return {
        isNull: true
      };
    }
  }

  public validatedRuc({ value }: FormControl): { [error: string]: boolean } {
    if (!value) { return; }
    const inicio = value.substr(0, 2);
    if (inicio.includes(10) || inicio.includes(15) || inicio.includes(17) || inicio.includes(20)) { return; } else { return {
      inValidRuc: true
    };
    }
  }

  public fechaEmision = (min: number) => (
    formControl: FormControl
  ): { descripcion: string } => {
    const errorFechaValidacion = this.fechaValidation(formControl);
    if (errorFechaValidacion) {
      return errorFechaValidacion;
    }
    const value = formControl.value;
    const filteredSlash = value.replace(REGEX_UNDERSCORE, '');
    const dateFormatted = this.getDateObject(filteredSlash);

    const date = this.convertFormattedToDate(dateFormatted);
    const now = new Date();
    this.setearTiempoACero(now);
    if ( date > now) {
      return {
        descripcion : 'La fecha debe ser máximo la fecha actual'
      };
    }

    const subtractDate = new Date();
    subtractDate.setFullYear(subtractDate.getFullYear() - min);
    this.setearTiempoACero(subtractDate);
    if ( subtractDate > date ) {
      return {
        descripcion: `La fecha debe ser menor a 12 meses`
      };
    }

    return null;
  };
}



// WEBPACK FOOTER //
// ./src/app/@compartidos/utils/validation.service.ts